import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import CurrentUserContext from 'contexts/CurrentUserContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import useMetricOptions from 'hooks/useMetricOptions';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import useGetMetricDatasets from 'components/Inputs/MetricPicker/MetricPickerPopup/hooks/useGetMetricDatasets';
import useGetDatasetLabel from 'components/Inputs/MetricPicker/MetricPickerPopup/hooks/useGetDatasetLabel';
import useSaveWidget from 'components/PerformanceBoards/hooks/useSaveWidget';
import useBonusMetricAssignments from 'components/PerformanceBoards/hooks/useBonusMetricAssignments';

import RelatedMetricDocumentsSlideoutWizard from './RelatedMetricDocumentsSlideoutWizard';

type DatasetGroup =
  PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsDatasetGroup;

const RelatedMetricDocumentsSlideoutWizardContainer = ({
  close,
  editingSection,
}: {
  close: () => void;
  editingSection:
    | PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsSection
    | undefined;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { datasets: allDatasets } = useContext(DatasetDefinitionsContext);
  const { metricOptionsLookup } = useMetricOptions();
  const { getFieldLabel } = useGetFieldLabel();
  const getDatasetLabel = useGetDatasetLabel();
  const getMetricDatasets = useGetMetricDatasets();
  const { assignments, byFieldName, isLoading } = useBonusMetricAssignments();

  const [id] = useState<string>(
    editingSection ? editingSection.id : getIdentifier(),
  );
  const [createdBy] = useState<string>(
    editingSection ? editingSection.createdBy : currentUserId,
  );
  const [createdOn] = useState<string>(
    editingSection ? editingSection.createdOn : getTimeStamp(),
  );
  const [title, setTitle] = useState<string>(
    editingSection ? editingSection.title : '',
  );
  const [bonusMetricFieldName, setBonusMetricFieldName] = useState<string>(
    editingSection ? editingSection.bonusMetricFieldName : '',
  );
  const [datasets, setDatasets] = useState<DatasetGroup[]>(
    editingSection ? editingSection.datasets : [],
  );
  const [section, setSection] = useState<
    PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsSection | undefined
  >();

  const saveWidget = useSaveWidget({ section, editingSection });

  const selectedAssignment = byFieldName[bonusMetricFieldName];
  const showDatePeriodAlert =
    !!selectedAssignment && selectedAssignment.datePeriodType !== 'to_date';

  const onTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setTitle(event.target.value);
  };

  const selectMetric = useCallback(
    (fieldName: string, metricId: string) => {
      setBonusMetricFieldName(fieldName);
      const metric = metricOptionsLookup[metricId];
      const metricDatasets = metric ? getMetricDatasets(metric) : [];
      setDatasets(
        metricDatasets.map((def) => ({
          dataType: def.type,
          heading: metricDatasets.length > 1 ? getDatasetLabel(def) : undefined,
          fields: [],
        })),
      );
    },
    [getDatasetLabel, getMetricDatasets, metricOptionsLookup],
  );

  const onHeadingChanged = useCallback((dataType: string, heading: string) => {
    setDatasets((current) =>
      current.map((g) => (g.dataType === dataType ? { ...g, heading } : g)),
    );
  }, []);

  const addField = useCallback((dataType: string, field: string) => {
    setDatasets((current) =>
      current.map((g) =>
        g.dataType === dataType && !g.fields.includes(field)
          ? { ...g, fields: [...g.fields, field] }
          : g,
      ),
    );
  }, []);

  const removeField = useCallback((dataType: string, field: string) => {
    setDatasets((current) =>
      current.map((g) =>
        g.dataType === dataType
          ? { ...g, fields: g.fields.filter((f) => f !== field) }
          : g,
      ),
    );
  }, []);

  const setFieldsOrder = useCallback((dataType: string, fields: string[]) => {
    setDatasets((current) =>
      current.map((g) => (g.dataType === dataType ? { ...g, fields } : g)),
    );
  }, []);

  const isMultiple = datasets.length > 1;

  useEffect(() => {
    const isValid =
      title !== '' &&
      !!selectedAssignment &&
      selectedAssignment.datePeriodType === 'to_date' &&
      datasets.length > 0 &&
      datasets.every((g) => g.fields.length > 0) &&
      (!isMultiple || datasets.every((g) => (g.heading ?? '').trim() !== ''));

    if (!isValid) {
      setSection(undefined);
      return;
    }

    const newSection: PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsSection =
      {
        id,
        createdBy,
        updatedBy: currentUserId,
        createdOn,
        updatedOn: getTimeStamp(),
        sectionType: 'Related Metric Documents',
        title,
        bonusMetricFieldName,
        datasets,
      };

    setSection(newSection);
  }, [
    bonusMetricFieldName,
    createdBy,
    createdOn,
    currentUserId,
    datasets,
    id,
    isMultiple,
    selectedAssignment,
    title,
  ]);

  const metricOptions = useMemo((): DropdownOption[] => {
    const nameCounts: { [name: string]: number } = {};
    assignments.forEach((a) => {
      const name = metricOptionsLookup[a.metricId]?.name;
      if (name) {
        nameCounts[name] = (nameCounts[name] ?? 0) + 1;
      }
    });

    return assignments.map((a) => {
      const name = metricOptionsLookup[a.metricId]?.name;
      const label = name
        ? nameCounts[name] > 1
          ? `${name} (${a.fieldName})`
          : name
        : a.fieldName;
      return {
        label,
        isSelected: a.fieldName === bonusMetricFieldName,
        onSelected: () => selectMetric(a.fieldName, a.metricId),
      };
    });
  }, [assignments, bonusMetricFieldName, metricOptionsLookup, selectMetric]);

  const selectedMetricLabel = useMemo(() => {
    if (!selectedAssignment) {
      return undefined;
    }
    return (
      metricOptionsLookup[selectedAssignment.metricId]?.name ??
      selectedAssignment.fieldName
    );
  }, [metricOptionsLookup, selectedAssignment]);

  const groups = useMemo(
    () =>
      datasets.map((g) => {
        const def = allDatasets.find((d) => d.type === g.dataType);
        const fieldOptions: DropdownOption[] = def
          ? def.fields
              .filter((f) => !g.fields.includes(f.field))
              .map((f) => ({
                label: getFieldLabel({ field: f.field, dataType: g.dataType }),
                isSelected: false,
                onSelected: () => addField(g.dataType, f.field),
              }))
          : [];
        return { ...g, fieldOptions };
      }),
    [addField, allDatasets, datasets, getFieldLabel],
  );

  return (
    <RelatedMetricDocumentsSlideoutWizard
      title={title}
      onTitleChanged={onTitleChanged}
      metricOptions={metricOptions}
      selectedMetricLabel={selectedMetricLabel}
      hasAssignmentSelected={!!selectedAssignment}
      showDatePeriodAlert={showDatePeriodAlert}
      isLoadingAssignments={isLoading}
      isMultiple={isMultiple}
      groups={groups}
      onHeadingChanged={onHeadingChanged}
      removeField={removeField}
      setFieldsOrder={setFieldsOrder}
      draftSection={section}
      saveWidget={saveWidget}
      close={close}
    />
  );
};

export default RelatedMetricDocumentsSlideoutWizardContainer;
